// A FieldSpec is a specification of all the metadata that pertains to a particular field,
// intended to be used with the Rich type system (e.g. RichType, RichForm) and the asset system,
// but without either as a hard requirement.
//
// A collection of specs (typically FieldSpecWithId[]) can drive:
//  - Editing - via RichForm, AssetForm or custom forms
//  - Presentation - see RichDisplayValue, and ToolFields for an example
//  - Validation - see validate.ts
//  - Storage - see api/assets
//  - API - TODO

import { AssetType } from 'lib/assets/asset'
import { pick } from 'lodash'
import { UnitShorthand } from 'types/unit-shorthand'
import { KindId, getDefaultConversionFactor, isKindNumeric } from './kind'

export type FieldSpec = {
  title: string
  inlineTitle?: string // Title when used in context with other fields and groups, typically a shorter title (like "X" instead of "X Length" if part of a "Lengths" group)
  description?: string // A longer description to be used for e.g. tooltips
  kind: KindId
  defaultValue?: any // Default value (mode agnostic)
  defaultValueIN?: any // Default value (for inch mode)
  defaultValueMM?: any // Default value (for metric mode)

  // onChange - called when the value of *this* field changes, allowing for dependent driven fields to be updated.
  // This is the opposite of compute, and allows having two-way dependent fields. When we get full dependency analysis
  // we can handle this in compute of the target fields instead, but right now that causes circular dependencies.
  onChange?: (
    values: any, // other values in object
    oldValue: any,
    newValue: any,
    setFieldValue: (name: string, value: any) => void, // callback to update other field values
  ) => void
  compute?: (values: any) => any
  dependencies?: readonly string[] // Field id's that compute depends on
  computeAlways?: boolean // Re-compute values even when user has set one

  comment?: string // Developer/API facing comment

  enumValues?: readonly EnumValue[] // for kind == "enum"

  itemSpecs?: readonly FieldSpecWithId[] // for kind == "array"
  itemKind?: KindId // for kind == "array" - simple type of items in the array, e.g. "text", "length" etc.

  assetType?: AssetType // for kind == "asset"
  //objectType?: string // for kind == "object"

  // Presentation hints
  hidden?: boolean // Not shown in UI representations
  hideChildren?: boolean // For 'asset' don't show child properties
  inGroup?: boolean // Inside current group
  noLabel?: boolean // Don't show label (e.g. for name field). Only for "wide" fields (text, enums etc.)
  forceNarrow?: boolean // Narrow override (text, enums etc.)
  showAsTable?: boolean // For 'array', show as a table
  componentId?: string // Hint for a custom component to use. (RichInput will look for a component with this id in the registry.) Use with a placeholder entry for any extra components in an otherwise standard layout.

  // Display units: override the default unit used for presentation (but not storage)
  displayUnit?: string // same unit for both systems
  displayUnitIN?: string // unit to display when using inch mode
  displayUnitMM?: string // unit to display when using metric mode

  // Validation
  required?: boolean // Must output value, even if equal to defaultValue
  gt?: number | string // Must be greater-than value or other property
  gte?: number | string // Must be greater-than-or-equal to value or other property
  lt?: number | string // Must be less-than value or other property
  lte?: number | string // Must be less-than-or-equal to value or other property
  maxLength?: number // Input characters max length (trimmed for whitespace)

  // Enabled state conditions. E.g.,
  // enabledWhen: { type: { in: [TypeEnum.OptA, TypeEnum.OptB] } }
  enabledWhen?: { [fieldName: string]: WhenCondition<any> }
}

export type FieldSpecWithId = {
  id: string
} & FieldSpec

export interface EnumValue {
  value: string | number
  title: string
}

export function getEnumValuesFromMap<T extends Record<string, string>>(
  map: T,
): EnumValue[] {
  return Object.entries(map).map(([value, title]) => ({
    value,
    title,
  }))
}

export function getEnumTitle(spec: FieldSpec, value) {
  return spec.enumValues?.find((entry) => entry.value == value)?.title || value
}

export type WhenCondition<T> = {
  in?: T[] // Matches if the property's value is in the provided array.
  notIn?: T[] // Matches if the property's value is not in the provided array.
  eq?: T // Matches if the property's value equals the provided value.
  notEq?: T // Matches if the property's value does not equal the provided value.
}

type FieldSpecWithIdAndChildren = FieldSpecWithId & {
  children?: FieldSpecWithId[]
}

export function groupFieldSpecs(
  items: readonly FieldSpecWithId[],
): FieldSpecWithIdAndChildren[] {
  const result: FieldSpecWithIdAndChildren[] = []
  let currentGroup: FieldSpecWithIdAndChildren | null = null
  let groupAdded = false // tracks if currentGroup has been pushed to result

  for (const item of items) {
    if (item.kind === 'group') {
      // Start a new group without adding it immediately
      currentGroup = { ...item, children: [] }
      groupAdded = false
    } else {
      if (item.inGroup !== false && currentGroup) {
        if (!groupAdded) {
          result.push(currentGroup)
          groupAdded = true
        }
        currentGroup.children!.push(item)
      } else {
        // Otherwise, push the item as a standalone entry.
        result.push(item)
        currentGroup = null
        groupAdded = false
      }
    }
  }

  return result
}

export function makeFieldSpecMap(
  specs: readonly FieldSpecWithId[],
): Record<string, FieldSpecWithId> {
  return specs.reduce((acc, item) => {
    acc[item.id] = item as FieldSpecWithId
    return acc
  }, {} as Record<string, FieldSpecWithId>)
}

function matchesCondition<T>(value: T, condition: WhenCondition<T>): boolean {
  if (condition.in !== undefined) {
    if (!condition.in.includes(value)) {
      return false
    }
  }
  if (condition.notIn !== undefined) {
    if (condition.notIn.includes(value)) {
      return false
    }
  }
  if (condition.eq !== undefined) {
    if (value !== condition.eq) {
      return false
    }
  }
  if (condition.notEq !== undefined) {
    if (value === condition.notEq) {
      return false
    }
  }
  return true
}

export function isFieldEnabled(
  spec: FieldSpec,
  data: Record<string, any>,
): boolean {
  if (!spec.enabledWhen) {
    return true // always enabled
  }

  // Check each dependency condition in enabledWhen.
  for (const [dependencyField, condition] of Object.entries(spec.enabledWhen)) {
    const dependencyValue = data[dependencyField]
    if (!matchesCondition(dependencyValue, condition)) {
      return false
    }
  }

  return true
}

export function filterEnabledFieldSpecs(
  data: Record<string, any>,
  specs: readonly FieldSpecWithId[],
) {
  return specs.filter((m) => isFieldEnabled(m, data))
}

export function buildUsedByMap(
  specs: readonly FieldSpecWithId[],
): Record<string, string[]> {
  const map: Record<string, string[]> = {}
  for (const { id, dependencies } of specs) {
    for (const dep of dependencies || []) {
      if (!map[dep]) {
        map[dep] = []
      }
      map[dep].push(id)
    }
  }
  return map
}

/**
 * Prepares asset data for storage. Fields are assumed to already be validated.
 */
export function prepareAssetData(
  source: Record<string, any>,
  specs: readonly FieldSpecWithId[],
) {
  const enabledSpecs = filterEnabledFieldSpecs(source, specs)
  const data = {}
  for (const spec of enabledSpecs) {
    data[spec.id] = source[spec.id]
  }
  return data
}

export function getDefaultValue(spec: FieldSpec, metric: boolean) {
  // Try defaultValue first, then mm/in depending on metric, finally use the reverse and convert
  let value = spec.defaultValue
  if (value === undefined) {
    value = metric ? spec.defaultValueMM : spec.defaultValueIN
  }
  if (value === undefined && isKindNumeric(spec.kind)) {
    value = metric ? spec.defaultValueIN : spec.defaultValueMM
    if (value !== undefined) {
      value = (getDefaultConversionFactor(spec.kind, metric) || 1.0) * value
    }
  }
  if (value === undefined) {
    if (spec.required && !spec.compute) {
      throw new Error(`No default value for required property '${spec.title}'`)
    }
  }
  return value
}

export function getDefaults<T>(
  specs: readonly FieldSpecWithId[],
  metric: boolean,
  initial: Partial<T> = {},
): T {
  const data = { ...initial } as T
  for (const spec of specs) {
    if (!spec.id) {
      continue // No id - maybe a group
    }
    if (data[spec.id] !== undefined) {
      continue // Already set
    }

    data[spec.id] = getDefaultValue(spec, metric)
  }

  // After all fixed defaults have been set, fill in those with compute methods
  for (const spec of specs) {
    if (spec.compute) {
      if (data[spec.id] !== undefined) {
        // TODO: consider computeAlways ?
        continue // Already set
      }
      data[spec.id] = spec.compute(data)
    }
  }

  return data
}

// rounding to avoid numbers like 1.9999999999999993 because 1/25.4 and similar is not represented exactly in binary floating‑point
const ROUNDING_FACTOR = 1e9
export function convertAndRound(value, factor) {
  if (factor === 1) {
    return value
  }

  return Math.round(value * factor * ROUNDING_FACTOR) / ROUNDING_FACTOR
}

export function convertToUnitSystem(
  data: Record<string, any>,
  specs: readonly FieldSpecWithId[],
  toMetric: boolean,
  recurse: boolean = true, // traverse recursively to child objects like array, asset and object
) {
  const specmap = makeFieldSpecMap(filterEnabledFieldSpecs(data, specs))
  for (const key of Object.keys(data)) {
    const spec = specmap[key]
    if (!spec) {
      continue
    }

    if (recurse && spec.kind === 'array') {
      const arr = data[key]
      if (Array.isArray(arr)) {
        if (spec.itemSpecs) {
          for (const item of arr) {
            if (item && typeof item === 'object') {
              convertToUnitSystem(item, spec.itemSpecs, toMetric)
            }
          }
        } else if (spec.itemKind) {
          const factor = getDefaultConversionFactor(spec.itemKind, toMetric)
          if (factor) {
            for (let i = 0; i < arr.length; i++) {
              arr[i] = convertAndRound(arr[i], factor)
            }
          }
        }
      }
      continue
    }

    /* This causes TS errors in registry.ts - I need to investigate
    if (recurse && spec.kind === 'asset') {
      const registryEntry = ASSET_REGISTRY[spec.assetType]
      if (registryEntry?.fieldSpecs && data[key]) {
        convertToUnitSystem(data[key], registryEntry.fieldSpecs, toMetric)
      }
      continue
    }
    */

    const factor = getDefaultConversionFactor(spec.kind, toMetric)
    if (factor) {
      data[key] = convertAndRound(data[key], factor)
    }
  }

  if ('unit' in data) {
    data.unit = (toMetric ? 'mm' : 'in') as UnitShorthand
  }
}

/**
  Extracts fields from data based on provided specs
 */
export function pickDataBySpecs<T>(
  data: Record<string, any>,
  specs: readonly (FieldSpec & { id: keyof T | undefined })[],
) {
  const specIds = Object.values(specs)
    .map(({ id }) => id)
    .filter((id) => id)

  const res = pick(data, specIds)

  return res as Partial<T>
}
